import isEqual from 'lodash/isEqual';
import intersectionBy from 'lodash/intersectionBy';
import unionBy from 'lodash/unionBy';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import produce from 'immer';

import {ResourceType as PropertyFilterResourceType} from 'common/report/constants';
import {customPropertiesStore, CustomPropertyResourceType} from 'common/report/stores/custom-properties';
import {baseComparator} from 'common/util';
import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {Segmentation} from 'common/widgets/property-filter-menu/models/segfilter';
import {PropertyDefinition} from 'common/util/definitions-types';
import {renamePropertyOld} from 'common/report/util/properties';
import {getTempLookupTableMappings} from 'common/report/stores/lookup-tables';
import {PropertyType} from 'common/report/constants';
import {pluralize} from 'common/util/string';

import {
  BookmarkablePropertyItem,
  BookmarkablePropertyItemKeyMap,
  NonDataGroupPropertyItemIdentifierKeys,
  PropertyItemPersistedIdentifierKeys,
  StagedItemType,
} from './types';
import {ObjectSectionItem as ObjectPropertyItem, PropertySectionItem as PropertyItem} from './item-menu-types';

/**
 * Find intersection of Mixpanel properties present in all given property lists.
 * Checks equality via `name` prop.
 */
export function calculatePropertiesIntersection(propertyLists: PropertyItem[]) {
  // @ts-ignore - TODO: Argument of type 'PropertyItem' is not assignable to parameter of type 'ArrayLike<string>'.
  return intersectionBy(...propertyLists, `name`);
}

/**
 * Find union of Mixpanel properties present in all given property lists.
 * Checks equality via `name` prop.
 */
export function calculatePropertiesUnion(propertyLists: PropertyItem[]) {
  // @ts-ignore - TODO: Expected at least 1 arguments, but got 1 or more.
  return unionBy(...propertyLists, `name`);
}

export function sanitizeCustomProperty(customProperty) {
  return pick(customProperty, [
    `behavior`,
    `composedProperties`,
    `mappedDataGroupId`,
    `referencedDirectlyBy`,
    `referencedBy`,
    `referencedRawEventProperties`,
    `description`,
    `displayFormula`,
    `name`,
    `resourceType`,
    `propertyType`,
    `joinPropertyType`,
  ]);
}

export const CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING = {
  [CustomPropertyResourceType.Events]: PropertyFilterResourceType.Event,
  [CustomPropertyResourceType.People]: PropertyFilterResourceType.User,
};

export function storeCustomPropertyToProperty(customPropertyId): PropertyItem {
  let customProperty = customPropertiesStore.value[customPropertyId];
  customProperty = sanitizeCustomProperty(customProperty);
  const {name} = customProperty;
  let {mappedDataGroupId, propertyType, joinPropertyType} = customProperty;
  const resourceType = CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING[customProperty.resourceType] || null;

  if (isPersistedCustomProperty(customPropertyId)) {
    customPropertyId = Number(customPropertyId);
    const tempDataGroupId = getTempLookupTableMappings(resourceType, true)?.get(customPropertyId);
    if (tempDataGroupId) {
      mappedDataGroupId = tempDataGroupId;
      joinPropertyType = joinPropertyType || propertyType;
      propertyType = PropertyType.Dimension;
    }
  }

  return {
    customProperty,
    customPropertyId,
    dimensionDataGroupId: mappedDataGroupId,
    resourceType,
    propertyType,
    propertyDefaultType: propertyType,
    type: propertyType,
    joinPropertyType,
    value: name,
    label: name,
  };
}

/**
 * Compare if two properties are the same, ignoring typecast, changes in custom property definitions, etc...
 * NOTE: Even for inline custom properties, they must have the same temporary id to be considered the same.
 */
export function isSamePropertyReference(property1, property2): boolean {
  if (property1.customPropertyId || property2.customPropertyId) {
    return property1.customPropertyId === property2.customPropertyId;
  }
  const PROPERTY_KEY_FIELDS = [`resourceType`, `propertyName`];
  return isEqual(pick(property1, PROPERTY_KEY_FIELDS), pick(property2, PROPERTY_KEY_FIELDS));
}

/** @todo: [shawn] Join utils to a base type convertor */
export function propertyItemToObjectPropertyItem(propertyItem: PropertyItem): ObjectPropertyItem {
  const {
    dataGroupId,
    resourceType,
    propertyDefaultType,
    propertyObjectKey,
    tempDataGroupId,
    joinPropertyType,
    type,
    value,
    label,
  } = propertyItem;

  return {
    ...(dataGroupId ? {dataGroupId} : {}),
    ...(joinPropertyType ? {joinPropertyType} : {}),
    ...(tempDataGroupId ? {tempDataGroupId} : {}),
    type,
    label,
    resourceType,
    propertyDefaultType,
    propertyName: value,
    propertyType: type,
    ...(propertyObjectKey
      ? {
          propertyObjectKey,
        }
      : {}),
  } as ObjectPropertyItem;
}

/**
 * Generates a Segmentation format for user or event PropertyItem types
 * used in property menu. In general, Segmentation is what we use for bookmarks.
 *
 * TODO: Strongly type the input, theres a bug in the type of propertyItem
 * not transfering custom properties correctly.
 */
export function propertyItemToSegmentation(propertyItem, resourceType): Segmentation {
  const {dimensionDataGroupId, dataGroupId, value, type} = propertyItem;

  return {
    resourceType,
    propertyType: type,
    propertyName: value,
    ...(dataGroupId ? {dataGroupId} : dimensionDataGroupId && Array.isArray(value) ? {dimensionDataGroupId} : null),
    ...pick(
      propertyItem,
      `customPropertyId`,
      `customProperty`,
      `tempDataGroupId`,
      `propertyDefaultType`,
      `propertyObjectKey`,
      `joinPropertyType`,
      `unit`,
    ),
  };
}

/**
 * Generates a PropertyItem format for the properties menu from
 * the Segmentation format used in bookmarks usually.
 *
 * TODO: Strongly type the result, theres a bug in the type of propertyItem
 * not typing custom properties correctly.
 */
export function segmentationToPropertyItem(segment: Segmentation) {
  const propertyType = segment.propertyType || (segment.customProperty && segment.customProperty.propertyType);
  return {
    ...(segment.customPropertyId ? {customPropertyId: segment.customPropertyId} : {}),
    ...(segment.customProperty ? {customProperty: segment.customProperty} : {}),
    dataGroupId: segment.dataGroupId,
    ...(segment.tempDataGroupId ? {tempDataGroupId: segment.tempDataGroupId} : {}),
    ...(segment.joinPropertyType ? {joinPropertyType: segment.joinPropertyType} : {}),
    ...(segment.propertyObjectKey ? {propertyObjectKey: segment.propertyObjectKey} : {}),
    propertyDefaultType: segment.propertyDefaultType || propertyType,
    resourceType: segment.resourceType,
    type: propertyType,
    value: segment.propertyName,
  };
}

export function sortUiUniquePropertyItems(items: PropertyItem[]) {
  return items.sort(baseComparator({order: `desc`, transform: (item: PropertyItem) => item.uiUniqueQueryCount}));
}

export function getDataDefinitionsPropertyMenuItem(propertyDefinition: PropertyDefinition, includeSubLabel?: boolean) {
  const returnObj = {
    ...propertyDefinition,
    label: propertyDefinition.displayName || renamePropertyOld(propertyDefinition.name),
    value: propertyDefinition.name,
    searchableContent: [propertyDefinition?.description, propertyDefinition?.name].join(` `),
    ...(propertyDefinition.join_property_type ? {joinPropertyType: propertyDefinition.join_property_type} : {}),
    ...(propertyDefinition.dimension_data_group_id
      ? {dimensionDataGroupId: propertyDefinition.dimension_data_group_id}
      : {}),
  };
  if (includeSubLabel && propertyDefinition?.uiUniqueQueryCount > 0) {
    const subLabel = `Used by ${propertyDefinition.uiUniqueQueryCount} ${pluralize(
      `teammate`,
      propertyDefinition.uiUniqueQueryCount,
    )} recently`;
    return {
      ...returnObj,
      subLabel,
    };
  } else {
    return returnObj;
  }
}

export function isSameProperty(property1: PropertyItem, property2: PropertyItem): boolean {
  return getItemUniqueKey(property1) === getItemUniqueKey(property2);
}

export function getItemUniqueKey(item: PropertyItem) {
  // ignore tempDataGroupId and dataGroupId when it's a join key
  const primaryKeys =
    item.type === `dimension` ? NonDataGroupPropertyItemIdentifierKeys : PropertyItemPersistedIdentifierKeys;
  const primaryKey = primaryKeys.find((key) => Boolean(item[key]));

  const {tempDataGroupId, customPropertyId = ``, dataGroupId, resourceType = ``, value} = item;
  switch (primaryKey) {
    case `tempDataGroupId`:
      return `temp${tempDataGroupId}::${customPropertyId}::${resourceType}::${value}`;
    case `dataGroupId`:
      return `${dataGroupId}::${customPropertyId}::${resourceType}::${value}`;
    case `customPropertyId`:
      return String(customPropertyId);
    default:
      return `${resourceType}::${item[primaryKey]}`;
  }
}

export function inferStagedItemType(item: PropertyItem): StagedItemType {
  if (
    !item.unit &&
    (item.propertyDefaultType === PropertyType.Datetime || item.joinPropertyType === PropertyType.Object) &&
    item.type === PropertyType.Datetime
  ) {
    return StagedItemType.Datetime;
  }

  if (item.type === PropertyType.Dimension) {
    return StagedItemType.Dimension;
  }

  if (
    item.type === PropertyType.Object &&
    item.propertyDefaultType === PropertyType.Object &&
    !item.propertyObjectKey?.length
  ) {
    return StagedItemType.Object;
  }

  if (!isNil(item.stagedItemType)) {
    return item.stagedItemType;
  }
  return null;
}

export function filterToBookmarkablePropertyItem(item: PropertyItem): BookmarkablePropertyItem {
  return produce(item, (itemDraft: PropertyItem): void => {
    for (const key in itemDraft) {
      if (!BookmarkablePropertyItemKeyMap[key]) {
        delete itemDraft[key];
      }
    }
  });
}
